(() => {
  const cache = new Map();

  const refreshCarouselLayout = (card) => {
    const carousel = card?.closest('.Carousel');
    const instance = carousel && window.Flickity?.data(carousel);
    // The theme skips resize() when the window width stays unchanged.
    // Reloading existing cells also measures their updated height.
    if (instance) requestAnimationFrame(() => instance.reloadCells());
  };


  const disclosureSelector = '[data-product-card-related]';
  const disclosures = new WeakMap();
  const pendingLayouts = new Set();
  let layoutFrame = 0;
  let disclosureId = 0;

  const fitDisclosure = (switcher) => {
    const state = disclosures.get(switcher);
    if (!state || !switcher.isConnected) return;
    const { options, controls, toggle } = state;
    const width = options.getBoundingClientRect().width;
    if (!width) return;

    controls.forEach((control) => { control.hidden = false; });
    toggle.hidden = true;
    const gap = parseFloat(getComputedStyle(options).columnGap) || 0;
    const widths = controls.map((control) => control.getBoundingClientRect().width);
    const totalWidth = widths.reduce((sum, value) => sum + value, 0) + gap * (controls.length - 1);

    if (totalWidth <= width + 0.5) {
      toggle.setAttribute('aria-expanded', 'false');
      state.expanded = false;
      return;
    }

    toggle.hidden = false;
    // Measure the widest possible counter before reserving its place in the row.
    toggle.textContent = `+${controls.length}`;
    const counterWidth = toggle.getBoundingClientRect().width;
    let visibleCount = 0;
    let usedWidth = counterWidth;
    for (const controlWidth of widths) {
      if (usedWidth + gap + controlWidth > width + 0.5) break;
      usedWidth += gap + controlWidth;
      visibleCount += 1;
    }
    const remaining = controls.length - visibleCount;
    controls.forEach((control, index) => { control.hidden = !state.expanded && index >= visibleCount; });
    toggle.textContent = state.expanded ? '−' : `+${remaining}`;
    toggle.setAttribute('aria-expanded', String(state.expanded));
    toggle.setAttribute('aria-label', state.expanded
      ? switcher.dataset.fewerColorsLabel
      : switcher.dataset.moreColorsLabel.replace('__count__', String(remaining)));
  };

  const scheduleDisclosure = (switcher) => {
    pendingLayouts.add(switcher);
    if (layoutFrame) return;
    layoutFrame = requestAnimationFrame(() => {
      layoutFrame = 0;
      pendingLayouts.forEach(fitDisclosure);
      pendingLayouts.clear();
    });
  };

  const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver((entries) => {
    entries.forEach(({ target, contentRect }) => {
      const switcher = target.closest(disclosureSelector);
      const state = disclosures.get(switcher);
      // Only width changes require a new fit; expanding also changes height.
      if (state && state.width !== contentRect.width) {
        state.width = contentRect.width;
        scheduleDisclosure(switcher);
      }
    });
  }) : null;

  const initialiseDisclosure = (switcher) => {
    if (disclosures.has(switcher)) return;
    const options = switcher.querySelector('.ProductCardSwitcher__options');
    const toggle = options?.querySelector('[data-product-card-options-toggle]');
    const controls = [...(options?.querySelectorAll('[data-product-card-target]') || [])];
    if (!toggle || !controls.length) return;
    // Preserve the originating colour order when the card is replaced.
    options.id = `product-card-options-${++disclosureId}`;
    toggle.setAttribute('aria-controls', options.id);
    disclosures.set(switcher, { options, controls, toggle, expanded: toggle.getAttribute('aria-expanded') === 'true' });
    resizeObserver?.observe(options);
    fitDisclosure(switcher);
  };

  const visitDisclosures = (root, callback) => {
    if (root.nodeType !== 1 && root.nodeType !== 9) return;
    if (root.matches?.(disclosureSelector)) callback(root);
    root.querySelectorAll(disclosureSelector).forEach(callback);
  };

  const initialiseDisclosures = (root) => visitDisclosures(root, initialiseDisclosure);
  const disposeDisclosures = (root) => visitDisclosures(root, (switcher) => {
    const state = disclosures.get(switcher);
    if (!state) return;
    resizeObserver?.unobserve(state.options);
    pendingLayouts.delete(switcher);
    disclosures.delete(switcher);
  });

  const startDisclosures = () => {
    initialiseDisclosures(document);
    new MutationObserver((records) => {
      records.forEach(({ removedNodes, addedNodes }) => {
        removedNodes.forEach(disposeDisclosures);
        addedNodes.forEach(initialiseDisclosures);
      });
    }).observe(document.body, { childList: true, subtree: true });
    const refitAll = () => visitDisclosures(document, scheduleDisclosure);
    if (!resizeObserver) window.addEventListener('resize', refitAll);
    document.fonts?.ready.then(refitAll);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startDisclosures, { once: true });
  else startDisclosures();

  document.addEventListener('click', (event) => {
    const expandColors = event.target.closest('[data-product-card-colors-expand]');
    if (expandColors) {
      const group = expandColors.closest('.ProductCardSwitcher__options--color');
      const extraColors = group?.querySelectorAll('[data-product-card-extra-color]');
      if (!extraColors?.length) return;
      extraColors.forEach((control) => control.removeAttribute('hidden'));
      expandColors.setAttribute('aria-expanded', 'true');
      expandColors.setAttribute('hidden', '');
      refreshCarouselLayout(expandColors.closest('.ProductItem'));
      extraColors[0].focus({ preventScroll: true });
      return;
    }
    const toggle = event.target.closest('[data-product-card-options-toggle]');
    if (!toggle) return;
    event.preventDefault();
    const switcher = toggle.closest(disclosureSelector);
    const state = disclosures.get(switcher);
    if (!state) return;
    state.expanded = !state.expanded;
    fitDisclosure(switcher);
  });


  const getFragmentUrl = (handle, sectionId) => {
    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(`${root}products/${handle}`, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    if (window.Shopify?.theme?.id) url.searchParams.set('preview_theme_id', window.Shopify.theme.id);
    return url.toString();
  };

  const loadCard = async (handle, sectionId) => {
    const url = getFragmentUrl(handle, sectionId);

    if (!cache.has(url)) {
      const request = fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Product card request failed: ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const card = documentFragment.querySelector('[data-product-card-fragment] > .ProductItem');

          if (!card) throw new Error('Product card fragment is missing');
          const mediaTemplate = documentFragment.querySelector('template[data-product-card-media-template]');
          if (mediaTemplate) card.append(mediaTemplate.cloneNode(true));
          return card;
        })
        .catch((error) => {
          cache.delete(url);
          throw error;
        });

      cache.set(url, request);
    }

    return (await cache.get(url)).cloneNode(true);
  };

  const initialiseCarousel = (card) => {
    window.initProductCardMedia(card);
  };

  const setBusy = (card, busy) => {
    card.setAttribute('aria-busy', String(busy));
    card.querySelectorAll('[data-product-card-target]').forEach((button) => {
      button.disabled = busy;
      button.setAttribute('aria-busy', String(busy));
    });
  };

  const updateCollectionFilterState = (card) => {
    const switcher = card.querySelector('[data-product-card-switcher]');
    const cell = card.closest('[data-pops-product]');

    if (!cell || !switcher?.dataset.productCardFinish) return;
    cell.dataset.popsFinish = switcher.dataset.productCardFinish;
  };

  const announce = (switcher, message) => {
    const status = switcher.querySelector('[data-product-card-switcher-status]');
    if (status) status.textContent = message;
  };

  // Keep the originating colour positions when a full product card is replaced.
  // Finish controls still come from the newly rendered product, never the old card.
  const preserveColorOrder = (previousSwitcher, nextCard) => {
    const previousGroup = previousSwitcher.querySelector('.ProductCardSwitcher__options--color');
    const nextSwitcher = nextCard.querySelector('[data-product-card-switcher]');
    const nextGroup = nextSwitcher?.querySelector('.ProductCardSwitcher__options--color');
    if (!previousGroup || !nextGroup) return;

    const controls = [...nextGroup.querySelectorAll('[data-product-card-target]')];
    const remaining = new Set(controls);
    const ordered = [];
    const finishChanged = previousSwitcher.dataset.productCardFinish !== nextSwitcher.dataset.productCardFinish;
    const swatchKey = (control) => control.querySelector('.ProductCardSwitcher__swatch')
      ?.getAttribute('style')?.replace(/\s+/g, '').toLowerCase();

    previousGroup.querySelectorAll('[data-product-card-target]').forEach((previous) => {
      let match = controls.find((control) => remaining.has(control)
        && control.dataset.productCardTarget === previous.dataset.productCardTarget);
      if (!match && finishChanged) {
        const key = swatchKey(previous);
        const matches = key ? controls.filter((control) => remaining.has(control) && swatchKey(control) === key) : [];
        if (matches.length === 1) [match] = matches;
      }
      if (match) {
        ordered.push(match);
        remaining.delete(match);
      }
    });
    ordered.push(...remaining);

    const toggle = nextGroup.querySelector('[data-product-card-colors-expand]');
    ordered.forEach((control) => nextGroup.insertBefore(control, toggle));
    const previousToggle = previousGroup.querySelector('[data-product-card-colors-expand]');
    const expanded = previousToggle?.getAttribute('aria-expanded') === 'true';
    ordered.forEach((control, index) => {
      const extra = index >= 4;
      control.toggleAttribute('data-product-card-extra-color', extra);
      control.hidden = extra && !expanded;
    });
    if (toggle) {
      toggle.hidden = expanded;
      toggle.setAttribute('aria-expanded', String(expanded));
    }
  };

  const switchProduct = async (button) => {
    const currentCard = button.closest('.ProductItem');
    const switcher = button.closest('[data-product-card-switcher]');
    const handle = button.dataset.productCardTarget;
    const sectionId = switcher?.dataset.productCardFragmentSection;

    if (!currentCard || !switcher || !handle || !sectionId || button.getAttribute('aria-pressed') === 'true') return;

    if (currentCard.getAttribute('aria-busy') === 'true') return;
    setBusy(currentCard, true);
    announce(switcher, switcher.dataset.loadingLabel || '');

    try {
      const nextCard = await loadCard(handle, sectionId);
      if (currentCard.classList.contains('is-revealed')) {
        nextCard.classList.add('is-revealed');
        nextCard.style.visibility = 'visible';
      }
      const oldInfo = currentCard.querySelector('.ProductItem__Info');
      const nextInfo = nextCard.querySelector('.ProductItem__Info');
      ['ProductItem__Info--left', 'ProductItem__Info--center', 'ProductItem__Info--right'].forEach((name) => {
        nextInfo?.classList.toggle(name, oldInfo?.classList.contains(name) || false);
      });
      nextCard.classList.toggle('ProductItem--horizontal', currentCard.classList.contains('ProductItem--horizontal'));
      const oldPrice = currentCard.querySelector('.ProductItem__PriceList');
      nextCard.querySelector('.ProductItem__PriceList')?.classList.toggle(
        'ProductItem__PriceList--showOnHover', oldPrice?.classList.contains('ProductItem__PriceList--showOnHover') || false
      );
      // Keep the originating explicit group, including a route back to the first product.
      // Some existing Shopify relations are one-way; changing a card must not lose its controls.
      if (switcher.hasAttribute('data-product-card-related')) {
        const group = switcher.cloneNode(true);
        group.dataset.updatedLabel = nextCard.querySelector('[data-product-card-switcher]')?.dataset.updatedLabel
          || nextCard.querySelector('.ProductItem__Title')?.textContent.trim() || '';
        group.querySelectorAll('[data-product-card-target]').forEach((control) => {
          const active = control.dataset.productCardTarget === handle;
          control.disabled = false;
          control.setAttribute('aria-busy', 'false');
          control.setAttribute('aria-pressed', String(active));
          control.classList.toggle('is-active', active);
        });
        const targetSwitcher = nextCard.querySelector('[data-product-card-switcher]');
        if (targetSwitcher) targetSwitcher.replaceWith(group);
        else nextInfo?.append(group);
      }
      preserveColorOrder(switcher, nextCard);
      currentCard.querySelectorAll('.swiper').forEach((carousel) => carousel.swiper?.destroy(true, true));
      disposeDisclosures(currentCard);
      currentCard.replaceWith(nextCard);
      initialiseDisclosures(nextCard);
      const recommendationCell = nextCard.closest('product-card');
      if (recommendationCell) {
        recommendationCell.setAttribute('handle', handle);
      }
      initialiseCarousel(nextCard);
      refreshCarouselLayout(nextCard);
      updateCollectionFilterState(nextCard);

      const nextSwitcher = nextCard.querySelector('[data-product-card-switcher]');
      announce(nextSwitcher, nextSwitcher?.dataset.updatedLabel || '');

      const selectedControl = nextCard.querySelector(
        `[data-product-card-target="${CSS.escape(handle)}"][aria-pressed="true"]`
      );
      selectedControl?.setAttribute('data-tooltip-dismissed', '');
      selectedControl?.focus({ preventScroll: true });

      document.dispatchEvent(
        new CustomEvent('product-card:changed', {
          bubbles: true,
          detail: { handle, card: nextCard },
        })
      );
    } catch {
      setBusy(currentCard, false);
      announce(switcher, switcher.dataset.errorLabel || '');
    }
  };

  document.addEventListener('click', (event) => {
    const finishLink = event.target.closest('[data-product-finish-variant-map]');
    if (finishLink) {
      const form = finishLink.closest('form');
      const variantId = form?.querySelector('[name="id"]')?.value;
      const nextId = JSON.parse(finishLink.dataset.productFinishVariantMap)[variantId];
      const url = new URL(finishLink.href);
      if (nextId) url.searchParams.set('variant', nextId);
      else url.searchParams.delete('variant');
      if (window.Shopify?.theme?.id) url.searchParams.set('preview_theme_id', window.Shopify.theme.id);
      finishLink.href = url.toString();
      return;
    }
    const button = event.target.closest('[data-product-card-target]');
    if (!button) return;

    event.preventDefault();
    switchProduct(button);
  });

  document.addEventListener('pointerover', (event) => {
    const button = event.target.closest('[data-product-card-target]');
    const switcher = button?.closest('[data-product-card-switcher]');
    const handle = button?.dataset.productCardTarget;
    const sectionId = switcher?.dataset.productCardFragmentSection;

    if (handle && sectionId && button.getAttribute('aria-pressed') !== 'true') {
      loadCard(handle, sectionId).catch(() => {});
    }
  });

  document.addEventListener('focusin', (event) => {
    const button = event.target.closest('[data-product-card-target]');
    const switcher = button?.closest('[data-product-card-switcher]');
    const handle = button?.dataset.productCardTarget;
    const sectionId = switcher?.dataset.productCardFragmentSection;

    if (handle && sectionId && button.getAttribute('aria-pressed') !== 'true') {
      loadCard(handle, sectionId).catch(() => {});
    }
  });
})();
